import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { DEALER_SETTINGS_DEFAULT } from 'constants/constants';
import DealerLayout from 'components/Layout/Dealer/DealerLayout';
import { useTranslation } from 'react-i18next';
import { Spinner2 } from 'components';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useUpdateDealerSettingsMutation } from 'thunks/RTKQuery/user';
import { authSlice, usersSlice } from 'slices';
import { IDealerSettings } from 'pages/Shared/Dealer/Models/dealers';
import { IUser } from 'interfaces/user';
import { handleRTKResponseToaster } from 'utils/handle-api-response';
import { LanguageComponent } from './LanguageComponent';
import { PageSizeComponent } from './PageSizeComponent';
import { NotificationsComponent } from './NotificationsComponent';

export const DealerSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data: authData } = useAppSelector((state) => state.auth);
  const rootDealer = authData?.dealer;
  const userSettings: any = authData?.user?.userSettings;

  const [dealerSettings, setDealerSettings] = useState<IDealerSettings>(
    DEALER_SETTINGS_DEFAULT
  );

  useEffect(() => {
    setDealerSettings({
      language: userSettings?.language,
      pageSize: +userSettings?.pageSize,
      notificationOnTicketsExpiry: userSettings?.notificationOnTicketsExpiry,
      notificationBeforeTicketExpiryInDays:
        +userSettings?.notificationBeforeTicketExpiryInDays ||
        +DEALER_SETTINGS_DEFAULT.notificationBeforeTicketExpiryInDays,
      notificationOccurrenceForTicketExpiry:
        userSettings?.notificationOccurrenceForTicketExpiry,
      includeExpiredProductsInTicketExpiryNotification:
        userSettings?.includeExpiredProductsInTicketExpiryNotification ?? true,
      includeExpiredProductOptionsInTicketExpiryNotification:
        userSettings?.includeExpiredProductOptionsInTicketExpiryNotification ??
        true,
    });
  }, [userSettings]);

  const [
    updateDealerSettings,
    {
      isLoading: dealerUpdateLoading,
      error: dealerUpdateError,
      status: dealerUpdateStatus,
    },
  ] = useUpdateDealerSettingsMutation();

  useEffect(() => {
    handleRTKResponseToaster(
      dealerUpdateStatus,
      dealerUpdateError,
      dispatch,
      t('contract.error'),
      t('dealer.settings.title'),
      t('dealer.settings.message')
    );
  }, [dealerUpdateStatus]);

  const handleChange = async (updatedSettings: IDealerSettings) => {
    if (!updatedSettings) return;
    const res = (await updateDealerSettings(updatedSettings)) as any;
    if (!res?.error) {
      setDealerSettings(updatedSettings);
      const userObj = res.data as IUser;
      if (updatedSettings.language !== userSettings.language) {
        dispatch(
          usersSlice.actions.updateUserLanguage(updatedSettings.language)
        );
      }
      dispatch(authSlice.actions.updateUser(userObj));
    }
  };

  return (
    <DealerLayout>
      <h1>{t('dealerManagement.settings.title')}</h1>
      <hr />
      <Spinner2 show={dealerUpdateLoading} />
      <Row className="mt-10">
        <Col md={4}>
          <PageSizeComponent
            dealerSettings={dealerSettings}
            handleChange={handleChange}
          />
        </Col>
        <Col md={4}>
          <LanguageComponent
            dealerSettings={dealerSettings}
            handleChange={handleChange}
          />
        </Col>
        <Col md={4} />
      </Row>
      <Row className="align-items-center">
        <Col className="mb-40 mt-40" md={4}>
          <h4 className="mb-42">{t('account.settings.notifications.title')}</h4>
          <NotificationsComponent
            rootDealer={rootDealer}
            dealerSettings={dealerSettings}
            handleChange={handleChange}
          />
        </Col>
        <Col md={8} />
      </Row>
    </DealerLayout>
  );
};
